import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Trophy, TrendingUp, Target, Search, Award, HelpCircle, Compass, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ContinueLearningHero } from '@/components/my-courses/ContinueLearningHero';
import { CourseProgressCard } from '@/components/my-courses/CourseProgressCard';
import { CourseCompletionCelebration } from '@/components/my-courses/CourseCompletionCelebration';
import { useMyCourses } from '@/hooks/useMyCourses';
import { getModuleRoute, getCourseRoute } from '@/utils/categoryRouteMapping';

interface CompletionData {
  courseId: string;
  courseName: string;
  completedAt?: string;
}

const MyCourses: React.FC = () => {
  const navigate = useNavigate();
  const { courses, continueLearningCourse, stats, loading, getModuleRouteForCourse } = useMyCourses();
  const [celebrationData, setCelebrationData] = useState<CompletionData | null>(null);

  // Check for course completion on mount
  useEffect(() => {
    const stored = localStorage.getItem('course_completed');
    if (stored) {
      try {
        setCelebrationData(JSON.parse(stored));
        localStorage.removeItem('course_completed');
      } catch (e) {
        console.error('Error parsing completion data:', e);
      }
    }
  }, []);

  // Listen for real-time completion events
  useEffect(() => {
    const handleCompletion = (e: CustomEvent<CompletionData>) => {
      setCelebrationData(e.detail);
    };
    
    window.addEventListener('course-completed', handleCompletion as EventListener);
    return () => window.removeEventListener('course-completed', handleCompletion as EventListener);
  }, []);

  const handleContinueLearning = () => {
    if (!continueLearningCourse) return;
    
    // Navigate to last accessed module or first incomplete module
    if (continueLearningCourse.lastAccessedModuleId) {
      const route = getModuleRouteForCourse(
        continueLearningCourse.id,
        continueLearningCourse.lastAccessedModuleId,
        continueLearningCourse.category
      );
      navigate(route);
    } else {
      navigate(continueLearningCourse.courseRoute);
    }
  };

  const handleCourseClick = (course: typeof courses[0]) => {
    if (course.lastAccessedModuleId && course.status !== 'not_started') {
      const route = getModuleRouteForCourse(course.id, course.lastAccessedModuleId, course.category);
      navigate(route);
    } else {
      navigate(course.courseRoute);
    }
  };

  const handleModuleClick = (course: typeof courses[0], moduleId: string) => {
    const route = getModuleRouteForCourse(course.id, moduleId, course.category);
    navigate(route);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 space-y-8">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Course Completion Celebration Modal */}
      <CourseCompletionCelebration
        isOpen={!!celebrationData}
        onClose={() => setCelebrationData(null)}
        courseId={celebrationData?.courseId || ''}
        courseName={celebrationData?.courseName || ''}
        completedAt={celebrationData?.completedAt}
      />

      <div className="container mx-auto py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">My Learning</h1>
            <p className="text-muted-foreground mt-1">
              Track your progress and continue where you left off
            </p>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link to="/courses">
              <Compass className="h-4 w-4" />
              Browse All Courses
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link to="/search-courses">
              <Search className="h-4 w-4" />
              Search Courses
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link to="/certificates">
              <Award className="h-4 w-4" />
              View Certificates
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link to="/learning-paths">
              <Sparkles className="h-4 w-4" />
              Learning Paths
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link to="/student-help">
              <HelpCircle className="h-4 w-4" />
              Help Guide
            </Link>
          </Button>
        </div>

        {/* Continue Learning Hero */}
        {continueLearningCourse && (
          <ContinueLearningHero
            courseTitle={continueLearningCourse.title}
            courseCategory={continueLearningCourse.category}
            moduleTitle={continueLearningCourse.lastAccessedModuleId 
              ? `Module ${continueLearningCourse.lastAccessedModuleId.split('-').pop()}`
              : undefined
            }
            progress={continueLearningCourse.progress}
            completedModules={continueLearningCourse.completedModules}
            totalModules={continueLearningCourse.totalModules}
            lastAccessedAt={continueLearningCourse.lastAccessedAt}
            onContinue={handleContinueLearning}
          />
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalCourses}</p>
                <p className="text-xs text-muted-foreground">Total Courses</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <TrendingUp className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Trophy className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Target className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.overallProgress}%</p>
                <p className="text-xs text-muted-foreground">Overall Progress</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Grid */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Your Courses</h2>
          
          {courses.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Courses Yet</h3>
                <p className="text-sm text-muted-foreground mb-4 text-center">
                  Start your learning journey by enrolling in a course
                </p>
                <button
                  onClick={() => navigate('/courses')}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Browse Courses
                </button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <CourseProgressCard
                  key={course.id}
                  courseId={course.id}
                  title={course.title}
                  category={course.category}
                  progress={course.progress}
                  completedModules={course.completedModules}
                  totalModules={course.totalModules}
                  modules={course.modules}
                  lastAccessedAt={course.lastAccessedAt}
                  quizzesPassed={course.quizzesPassed}
                  totalQuizzes={course.totalQuizzes}
                  currentModuleId={course.lastAccessedModuleId}
                  status={course.status}
                  onContinue={() => handleCourseClick(course)}
                  onModuleClick={(moduleId) => handleModuleClick(course, moduleId)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyCourses;
